import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/legal/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions — BidNova Auctions" },
      {
        name: "description",
        content:
          "Terms & Conditions for BidNova Auctions, a UK graded Pokémon card auction platform governed by English law.",
      },
      { property: "og:title", content: "Terms & Conditions — BidNova Auctions" },
      {
        property: "og:description",
        content: "The legal terms governing your use of the BidNova Auctions platform.",
      },
      { property: "og:url", content: "https://bidnovaauctions.uk/legal/terms" },
      { property: "og:type", content: "article" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/legal/terms" }],
    scripts: [
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Legal", path: "/legal/terms" },
        { name: "Terms of Service", path: "/legal/terms" },
      ]),
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  const { t } = useTranslation("legal");
  const html = (key: string, fallback: string) => ({ __html: t(key, fallback) });
  return (
    <LegalPage
      eyebrow={t("eyebrow", "Legal")}
      title={t("terms.title", "Terms & Conditions")}
      updated="7 July 2026"
    >
      <p
        dangerouslySetInnerHTML={html(
          "terms.intro",
          'These Terms &amp; Conditions ("Terms") form a binding agreement between you and BidNova Auctions Ltd, a company registered in England &amp; Wales ("we", "us", "our"). By registering, browsing, bidding or listing on bidnovaauctions.uk (the "Platform"), you agree to these Terms.',
        )}
      />

      <h2>{t("terms.s1.h", "1. About us and this agreement")}</h2>
      <p
        dangerouslySetInnerHTML={html(
          "terms.s1.p",
          "We operate an online auction platform for graded and ungraded trading cards, principally Pokémon. We act as an intermediary between buyers and sellers and are not a party to the contract of sale, save that we collect payment on behalf of sellers and hold it until the transaction is complete. These Terms are governed by the laws of England &amp; Wales and the courts of England &amp; Wales have exclusive jurisdiction.",
        )}
      />

      <h2>{t("terms.s2.h", "2. Eligibility and account registration")}</h2>
      <ul>
        <li>
          {t(
            "terms.s2.li1",
            "You must be at least 18 years old and have legal capacity to enter into contracts under English law.",
          )}
        </li>
        <li>
          {t(
            "terms.s2.li2",
            "You must provide accurate registration information and keep it up to date.",
          )}
        </li>
        <li>
          {t(
            "terms.s2.li3",
            "You are responsible for all activity on your account. Keep your password confidential.",
          )}
        </li>
        <li
          dangerouslySetInnerHTML={html(
            "terms.s2.li4",
            'You must accept these Terms and our <a href="/legal/privacy">Privacy Policy</a> during registration.',
          )}
        />
        <li>
          {t(
            "terms.s2.li5",
            "We may suspend or terminate accounts that breach these Terms, engage in fraud, or harm other users.",
          )}
        </li>
      </ul>

      <h2>{t("terms.s3.h", "3. Auctions — how bidding works")}</h2>
      <ul>
        <li>
          {t(
            "terms.s3.li1",
            "Every bid is a legally binding offer to buy the lot at the amount bid, subject to any reserve.",
          )}
        </li>
        <li>
          {t(
            "terms.s3.li2",
            "Bids cannot be retracted once placed. Placing a bid you cannot honour is a breach of these Terms.",
          )}
        </li>
        <li>
          {t(
            "terms.s3.li3",
            "Bids must equal or exceed the current bid plus the minimum bid increment shown on the lot.",
          )}
        </li>
        <li>
          {t(
            "terms.s3.li4",
            "Sellers may set a reserve price. If bidding does not reach the reserve, no contract of sale is formed.",
          )}
        </li>
        <li>
          {t(
            "terms.s3.li5",
            'To prevent last-second "sniping", any bid placed in the final two minutes extends the auction by two minutes.',
          )}
        </li>
        <li>
          {t(
            "terms.s3.li6",
            'Sellers may not bid on their own auctions, directly or through third parties ("shill bidding" is prohibited).',
          )}
        </li>
        <li>
          {t(
            "terms.s3.li7",
            "Where a winning bid meets the reserve, a binding contract is formed between the buyer and the seller.",
          )}
        </li>
      </ul>

      <h2>{t("terms.s4.h", "4. Payments and platform fees")}</h2>
      <ul>
        <li>{t("terms.s4.li1", "All prices and fees are in Pounds Sterling (GBP, £).")}</li>
        <li>
          {t(
            "terms.s4.li2",
            "Winning bidders must complete payment within 72 hours via our Stripe checkout.",
          )}
        </li>
        <li>
          {t(
            "terms.s4.li3",
            "We collect payment on the seller's behalf and hold funds until delivery is confirmed.",
          )}
        </li>
        <li>
          {t(
            "terms.s4.li4",
            "Our platform commission is 8% of the winning bid, deducted before payout to the seller. There are no listing fees, no monthly fees and no subscription fees — commission is only charged on successful sales.",
          )}
        </li>
        <li>
          {t(
            "terms.s4.li5",
            "Payment card processing is handled by Stripe Payments UK Ltd; you also agree to Stripe's terms.",
          )}
        </li>
        <li>
          {t(
            "terms.s4.li6",
            "Failure to pay may result in cancellation of the sale, negative account feedback, and suspension.",
          )}
        </li>
      </ul>

      <h2>{t("terms.s5.h", "5. Delivery, tracking and buyer confirmation")}</h2>
      <ul>
        <li>
          {t(
            "terms.s5.li1",
            "Sellers must dispatch within 5 business days of payment confirmation and upload a tracking number.",
          )}
        </li>
        <li>
          {t(
            "terms.s5.li2",
            "All UK shipments must use a tracked and signed-for service (e.g. Royal Mail Special Delivery).",
          )}
        </li>
        <li>
          {t(
            "terms.s5.li3",
            "Buyers should confirm delivery via the Platform once the item is received and inspected.",
          )}
        </li>
        <li>
          {t(
            "terms.s5.li4",
            "If no dispute is opened within 72 hours of delivery, the order is automatically deemed accepted.",
          )}
        </li>
      </ul>

      <h2>{t("terms.s6.h", "6. Buyer protection")}</h2>
      <p>
        {t(
          "terms.s6.p",
          "Because you buy from a private or business seller, statutory rights under the Consumer Rights Act 2015 apply only where the seller is a trader. In addition, we operate the following Platform protection for every purchase:",
        )}
      </p>
      <ul>
        <li>
          {t(
            "terms.s6.li1",
            "Payments are processed securely through Stripe. Seller payouts are released in accordance with BidNova Auctions’ payout policy.",
          )}
        </li>
        <li>
          {t(
            "terms.s6.li2",
            "You may open a dispute if the item is not as described, is damaged, or does not arrive.",
          )}
        </li>
        <li>
          {t(
            "terms.s6.li3",
            "Where an admin resolves a dispute in your favour, we will refund you and recover funds from the seller.",
          )}
        </li>
        <li>
          {t(
            "terms.s6.li4",
            "Grading disputes concerning PSA, BGS or CGC slabs are resolved by reference to the grading company's records.",
          )}
        </li>
      </ul>

      <h2>{t("terms.s7.h", "7. Seller obligations (summary)")}</h2>
      <p
        dangerouslySetInnerHTML={html(
          "terms.s7.p",
          'Full seller obligations are set out in the <a href="/legal/seller-agreement">Seller Agreement</a>. In summary, sellers must accurately describe every lot, disclose defects, own the item outright, ship promptly, and comply with UK consumer and tax law.',
        )}
      />

      <h2>{t("terms.s8.h", "8. Prohibited items and conduct")}</h2>
      <p>
        {t("terms.s8.p1", "The following may not be listed, sold or promoted on the Platform:")}
      </p>
      <ul>
        <li>
          {t(
            "terms.s8.li1",
            "Counterfeit, forged or reproduction cards, altered slabs, or items with tampered grading labels.",
          )}
        </li>
        <li>{t("terms.s8.li2", "Stolen goods or goods you do not have the right to sell.")}</li>
        <li>
          {t(
            "terms.s8.li3",
            "Items infringing intellectual property rights of The Pokémon Company, Nintendo, or any third party.",
          )}
        </li>
        <li>{t("terms.s8.li4", "Items subject to UK export controls or trade sanctions.")}</li>
        <li>
          {t(
            "terms.s8.li5",
            "Weapons, drugs, adult content, or any item unlawful under English law.",
          )}
        </li>
      </ul>
      <p>
        {t(
          "terms.s8.p2",
          "You must also not: manipulate bidding, harass other users, scrape the Platform, or attempt to bypass fees.",
        )}
      </p>

      <h2>{t("terms.s9.h", "9. Disputes and resolution")}</h2>
      <ul>
        <li>
          {t(
            "terms.s9.li1",
            "Buyers or sellers may open a dispute through the order page, describing the issue in writing.",
          )}
        </li>
        <li>
          {t(
            "terms.s9.li2",
            "No payout will be released to a seller while a dispute is active on that order.",
          )}
        </li>
        <li>
          {t(
            "terms.s9.li3",
            "Our admin team will review evidence and communicate with both parties before deciding an outcome.",
          )}
        </li>
        <li>
          {t(
            "terms.s9.li4",
            "Possible resolutions include refund, release of funds to the seller, or partial settlement.",
          )}
        </li>
        <li
          dangerouslySetInnerHTML={html(
            "terms.s9.li5",
            "You retain your rights to pursue statutory remedies through the courts of England &amp; Wales.",
          )}
        />
      </ul>

      <h2>{t("terms.s10.h", "10. Fees, taxes and invoicing")}</h2>
      <p>
        {t(
          "terms.s10.p",
          "Sellers are responsible for their own tax affairs, including any VAT obligations and reporting income to HMRC. We may be required by HMRC to share transaction data under the OECD Model Reporting Rules for Digital Platforms.",
        )}
      </p>

      <h2>{t("terms.s11.h", "11. Intellectual property")}</h2>
      <p>
        {t(
          "terms.s11.p",
          "The Platform, including branding, design, and code, is owned by us or our licensors. You may not copy, resell, or reverse-engineer any part of the Platform without our written consent.",
        )}
      </p>

      <h2>{t("terms.s12.h", "12. Limitation of liability")}</h2>
      <p>
        {t(
          "terms.s12.p",
          "Nothing in these Terms limits liability for death or personal injury caused by negligence, for fraud, or for any liability that cannot be limited under English law. Subject to that, our aggregate liability to you in connection with the Platform is limited to the greater of £100 or the total fees you have paid us in the 12 months preceding the event giving rise to the claim.",
        )}
      </p>

      <h2>{t("terms.s13.h", "13. Changes to these Terms")}</h2>
      <p>
        {t(
          "terms.s13.p",
          "We may update these Terms from time to time. Material changes will be notified to you by email or on-Platform notice at least 14 days before they take effect. Your continued use after the effective date constitutes acceptance.",
        )}
      </p>

      <h2>{t("terms.s14.h", "14. Contact")}</h2>
      <p
        dangerouslySetInnerHTML={html(
          "terms.s14.p",
          'Questions about these Terms: <a href="mailto:legal@bidnovaauctions.uk">legal@bidnovaauctions.uk</a>.',
        )}
      />
    </LegalPage>
  );
}
